import { apiClient } from "@/lib/api/client";
import { buildQueryString } from "@/lib/utils/query-string";
import type {
  AddProjectContactPayload,
  CreateChangeOrderPayload,
  CreateAdjustmentPayload,
  CreatePhasePayload,
  CreateProjectPayload,
  CreateRoomPayload,
  PaginatedResponse,
  Project,
  ProjectChangeOrder,
  ProjectChangeOrderApprovalResult,
  ProjectContact,
  ProjectListItem,
  ProjectListParams,
  ProjectPhase,
  ProjectPhaseAdjustment,
  ProjectRoom,
  ProjectRoomAdjustment,
  ProjectTotals,
  SiteNotesDraft,
  SiteNotesDraftListItem,
  SiteNotesMessage,
  UpdateChangeOrderPayload,
  UpdateAdjustmentPayload,
  UpdatePhasePayload,
  UpdateProjectContactPayload,
  UpdateProjectPayload,
  UpdateRoomPayload,
  VersionHistoryItem,
} from "../types/project.types";

const BASE = "/api/projects/";

// ─── Projects ───────────────────────────────────────────────────────────

export async function listProjects(
  params: ProjectListParams = {},
): Promise<PaginatedResponse<ProjectListItem>> {
  const qs = buildQueryString(params as Record<string, unknown>);
  const { data } = await apiClient.get(`${BASE}?${qs}`);
  return data;
}

export async function getProject(id: number): Promise<Project> {
  const { data } = await apiClient.get(`${BASE}${id}/`);
  return data;
}

export async function createProject(
  payload: CreateProjectPayload,
): Promise<Project> {
  const { data } = await apiClient.post(BASE, payload);
  return data;
}

export async function updateProject(
  id: number,
  payload: UpdateProjectPayload,
): Promise<Project> {
  const { data } = await apiClient.patch(`${BASE}${id}/`, payload);
  return data;
}

export async function deleteProject(id: number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/`);
}

export async function returnProjectToDraft(id: number): Promise<Project> {
  const { data } = await apiClient.post(`${BASE}${id}/return-to-draft/`, {});
  return data;
}

export async function listProjectVersionHistory(
  projectId: number,
): Promise<VersionHistoryItem[]> {
  const { data } = await apiClient.get(`${BASE}${projectId}/version-history/`);
  return data;
}

export async function listProjectChangeOrders(
  projectId: number,
): Promise<ProjectChangeOrder[]> {
  const { data } = await apiClient.get(`${BASE}${projectId}/change-orders/`);
  return data;
}

export async function createProjectChangeOrder(
  projectId: number,
  payload: CreateChangeOrderPayload,
): Promise<ProjectChangeOrder> {
  const { data } = await apiClient.post(`${BASE}${projectId}/change-orders/`, payload);
  return data;
}

export async function updateProjectChangeOrder(
  projectId: number,
  id: number,
  payload: UpdateChangeOrderPayload,
): Promise<ProjectChangeOrder> {
  const { data } = await apiClient.patch(`${BASE}${projectId}/change-orders/${id}/`, payload);
  return data;
}

export async function approveProjectChangeOrder(
  projectId: number,
  id: number,
): Promise<ProjectChangeOrderApprovalResult> {
  const { data } = await apiClient.post(`${BASE}${projectId}/change-orders/${id}/approve/`);
  return data;
}

// ─── Project Contacts ───────────────────────────────────────────────────

export async function listProjectContacts(
  projectId: number,
): Promise<ProjectContact[]> {
  const { data } = await apiClient.get(`${BASE}${projectId}/contacts/`);
  return data;
}

export async function addProjectContact(
  projectId: number,
  payload: AddProjectContactPayload,
): Promise<ProjectContact> {
  const { data } = await apiClient.post(
    `${BASE}${projectId}/contacts/`,
    payload,
  );
  return data;
}

export async function updateProjectContact(
  projectId: number,
  id: number,
  payload: UpdateProjectContactPayload,
): Promise<ProjectContact> {
  const { data } = await apiClient.patch(
    `${BASE}${projectId}/contacts/${id}/`,
    payload,
  );
  return data;
}

export async function removeProjectContact(
  projectId: number,
  id: number,
): Promise<void> {
  await apiClient.delete(`${BASE}${projectId}/contacts/${id}/`);
}

export async function setPrimaryProjectContact(
  projectId: number,
  id: number,
): Promise<ProjectContact> {
  const { data } = await apiClient.post(
    `${BASE}${projectId}/contacts/${id}/set-primary/`,
  );
  return data;
}

// ─── Site Notes (AI assistant) ──────────────────────────────────────────

/** One-shot: runs the formatting agent and commits the result to
 *  ``Project.access_site_notes`` in the same backend call. Returns the
 *  formatted text the agent produced (so the UI can surface it in a
 *  toast) and the draft_id the call closed. Replaces the prior two-call
 *  flow (`finalizeSiteNotes` for preview + `commitSiteNotesDraft` for
 *  commit), which had a UX failure mode where users would click the
 *  first button and assume the project had been saved. */
export async function finalizeAndSaveSiteNotes(
  projectId: number,
  history: SiteNotesMessage[],
  existingNotes: string,
): Promise<{ formatted_text: string; draft_id: number | null }> {
  const { data } = await apiClient.post(
    `${BASE}${projectId}/access-notes/finalize-and-save/`,
    { history, existing_notes: existingNotes },
  );
  return data;
}

export async function getActiveSiteNotesDraft(
  projectId: number,
): Promise<SiteNotesDraft | null> {
  const response = await apiClient.get(
    `${BASE}${projectId}/access-notes/draft/active/`,
  );
  // 204 No Content → no active draft for this user/project.
  if (response.status === 204) return null;
  return response.data;
}

export async function listSiteNotesDrafts(
  projectId: number,
): Promise<SiteNotesDraftListItem[]> {
  const { data } = await apiClient.get(
    `${BASE}${projectId}/access-notes/drafts/`,
  );
  return data;
}

export async function getSiteNotesDraft(
  projectId: number,
  draftId: number,
): Promise<SiteNotesDraft> {
  const { data } = await apiClient.get(
    `${BASE}${projectId}/access-notes/drafts/${draftId}/`,
  );
  return data;
}

export async function discardSiteNotesDraft(
  projectId: number,
  draftId: number,
): Promise<void> {
  await apiClient.post(
    `${BASE}${projectId}/access-notes/drafts/${draftId}/discard/`,
  );
}

export interface ActiveSiteNotesDraftSummary {
  id: number;
  project: { id: number; name: string };
  preview: string;
  message_count: number;
  created_at: string;
  updated_at: string;
}

export async function listActiveSiteNotesDraftsForUser(): Promise<
  ActiveSiteNotesDraftSummary[]
> {
  const { data } = await apiClient.get(`${BASE}site-notes-drafts/active/`);
  return data;
}

// ─── Phases / Rooms / Adjustments ───────────────────────────────────────

export async function listProjectPhases(
  projectId: number,
): Promise<ProjectPhase[]> {
  const { data } = await apiClient.get(`${BASE}${projectId}/phases/`);
  return data;
}

export async function createPhase(
  projectId: number,
  payload: CreatePhasePayload,
): Promise<ProjectPhase> {
  const { data } = await apiClient.post(`${BASE}${projectId}/phases/`, payload);
  return data;
}

export async function updatePhase(
  id: number,
  payload: UpdatePhasePayload,
): Promise<ProjectPhase> {
  const { data } = await apiClient.patch(`${BASE}phases/${id}/`, payload);
  return data;
}

export async function deletePhase(id: number): Promise<void> {
  await apiClient.delete(`${BASE}phases/${id}/`);
}

export async function reorderPhases(
  projectId: number,
  orderedIds: number[],
): Promise<ProjectPhase[]> {
  const { data } = await apiClient.post(`${BASE}${projectId}/phases/reorder/`, {
    ordered_ids: orderedIds,
  });
  return data;
}

export async function listPhaseRooms(phaseId: number): Promise<ProjectRoom[]> {
  const { data } = await apiClient.get(`${BASE}phases/${phaseId}/rooms/`);
  return data;
}

export async function createRoom(
  phaseId: number,
  payload: CreateRoomPayload,
): Promise<ProjectRoom> {
  const { data } = await apiClient.post(
    `${BASE}phases/${phaseId}/rooms/`,
    payload,
  );
  return data;
}

export async function updateRoom(
  id: number,
  payload: UpdateRoomPayload,
): Promise<ProjectRoom> {
  const { data } = await apiClient.patch(`${BASE}rooms/${id}/`, payload);
  return data;
}

export async function deleteRoom(id: number): Promise<void> {
  await apiClient.delete(`${BASE}rooms/${id}/`);
}

export async function reorderRooms(
  phaseId: number,
  orderedIds: number[],
): Promise<ProjectRoom[]> {
  const { data } = await apiClient.post(
    `${BASE}phases/${phaseId}/rooms/reorder/`,
    { ordered_ids: orderedIds },
  );
  return data;
}

export async function moveRoomToPhase(
  roomId: number,
  targetPhaseId: number,
): Promise<ProjectRoom> {
  const { data } = await apiClient.post(`${BASE}rooms/${roomId}/move-to-phase/`, {
    target_phase_id: targetPhaseId,
  });
  return data;
}

export async function createRoomAdjustment(
  roomId: number,
  payload: CreateAdjustmentPayload,
): Promise<ProjectRoomAdjustment> {
  const { data } = await apiClient.post(
    `${BASE}rooms/${roomId}/adjustments/`,
    payload,
  );
  return data;
}

export async function updateRoomAdjustment(
  id: number,
  payload: UpdateAdjustmentPayload,
): Promise<ProjectRoomAdjustment> {
  const { data } = await apiClient.patch(
    `${BASE}adjustments/room/${id}/`,
    payload,
  );
  return data;
}

export async function deleteRoomAdjustment(id: number): Promise<void> {
  await apiClient.delete(`${BASE}adjustments/room/${id}/`);
}

export async function createPhaseAdjustment(
  phaseId: number,
  payload: CreateAdjustmentPayload,
): Promise<ProjectPhaseAdjustment> {
  const { data } = await apiClient.post(
    `${BASE}phases/${phaseId}/adjustments/`,
    payload,
  );
  return data;
}

export async function updatePhaseAdjustment(
  id: number,
  payload: UpdateAdjustmentPayload,
): Promise<ProjectPhaseAdjustment> {
  const { data } = await apiClient.patch(
    `${BASE}adjustments/phase/${id}/`,
    payload,
  );
  return data;
}

export async function deletePhaseAdjustment(id: number): Promise<void> {
  await apiClient.delete(`${BASE}adjustments/phase/${id}/`);
}

export async function getProjectTotals(
  projectId: number,
): Promise<ProjectTotals> {
  const { data } = await apiClient.get(`${BASE}${projectId}/totals/`);
  return data;
}
